/* eslint-disable @typescript-eslint/no-empty-interface */
import * as coreClient from "@azure/core-client";
import * as coreRestPipeline from "@azure/core-rest-pipeline";

export interface BasketItemRequest {
  /** Ticket Product ID */
  ticketID: number;
  /** Price of Basket Item with Quantity as 1 */
  itemPrice: number;
  /** Quantity of Basket Item */
  quantity: number;
  /** Payment Type of Price for the Basket Item */
  paymentMethodType: BasketItemRequestPaymentMethodType;
}

export interface Basket {
  /** UUID Generated Unique for Each Basket */
  basketId: string;
  /** Total Number of Product Items in Basket */
  totalItems: number;
  /** Total Price Value of All Product Items including the Quanity */
  basketTotal: number;
  /** Total Price Value To Pay for All Product Items including the Quanity and After Applying the Discount */
  toPayBasketTotal: number;
  /** Currency for Basket Total Value */
  currency?: BasketCurrency;
  basketItems: BasketItem[];
  discount?: BasketDiscount;
}

export interface BasketItem {
  /** Ticket Product ID */
  ticketID: number;
  /** Ticket Description based on Ticket ID or Ticket Code (Description might differ when Ticket fetched based on ID or Code) */
  ticketDescription?: string;
  /** Price of Basket Item with Quantity as 1 */
  itemPrice: number;
  /** Quantity of Basket Item */
  quantity: number;
  /** Total Price of Basket Item - Item Price multiply with provided Quanity  (temporary field to avoid breaking change and value same as subTotalPrice) */
  price?: number;
  /** Total Price of Basket Item - Item Price multiply with provided Quanity (itemPrice * quantity) */
  subTotalPrice: number;
  /** Total Price To Pay of Basket Item is Sub Total Price minus Discount Value for matched Ticket from Discounts Accepted (subTotalPrice - basketDiscount.accepted.discountValue) */
  toPayPrice: number;
  /** Payment Type of Price for the Basket Item */
  paymentMethodType: BasketItemPaymentMethodType;
  /** Ticket Period Name */
  ticketPeriodName?: string;
  /** Describes this Ticket Product is a Direct Debit ( true if the Ticket Product is Direct Debit Ticket ) */
  isDirectDebit?: boolean;
  /** Number of Days for '1-week' Ticket Product */
  noOfDays?: number;
  pricePerDay?: number;
  /** Minimum Age Band for a Ticket Product (If 0 - 'zero' no need to check age validation for Min Age, If positive value it is the Min Age limit) */
  minAgeBand?: number;
  /** Maximum Age Band for a Ticket Product (If 0 - 'zero' no need to check age validation for Max Age, If positive value it is the Max Age limit) */
  maxAgeBand?: number;
}

export interface BasketDiscount {
  /** Voucher Code To Be Applied for Discount */
  voucherCode: string;
  /** Total Discount Value To be Applied As Discount considering All Products in Cart (Basket) */
  totalDiscountValue: number;
  /** Discount Label to be Used for Display (will be received from Talon response) */
  discountLabel: string;
  accepted?: VoucherCodeDiscountAccepted[];
  /** Failure Reason when Voucher Code is Invalid or Discount is Not Applied due to Some Reason (will be received from Talon response) (Please Check Error Response Google Sheet To Match with TalonOne Responses) */
  discountRejectedReason?: BasketDiscountRejectedReason;
}

export interface VoucherCodeDiscountAccepted {
  /** Ticket Product ID */
  ticketID: number;
  /** Discount Value To be Applied As Discount On Product (will be received from Talon response) as 1.75 - 10% discount per Item Price * quantity */
  discountValue: number;
}

export interface ApiError {
  code?: number;
  shortDescription?: string;
  description?: string;
}

export interface BasketDeleteResponse {
  /** Success Message after Basket Deleted */
  message?: string;
}

export interface BasketItemUpdateRequest {
  /** Price of Basket Item with Quantity as 1 */
  itemPrice: number;
  /** Quantity of Basket Item */
  quantity: number;
  /** Payment Type of Price for the Basket Item */
  paymentMethodType: BasketItemUpdateRequestPaymentMethodType;
}

export interface BasketCheckoutRequest {
  applicants: Applicant[];
  billingCustomer: BillingCustomer;
  billingAddress: Address;
  directDebit?: DirectDebit;
  preferences: CheckoutPreferences;
  tandc: TermsAndConditions;
  redirectURLs: NotificationRedirect;
}

export interface Applicant {
  /** Ticket To Be Purchased For Whom */
  ticketFor: ApplicantTicketFor;
  /** Ticket Product ID */
  basketItemTicketID: string;
  /** Ticket Product Quantity Index */
  basketItemQuantityIndex: number;
  /** TicketCode is from Earlier Fares API (either mTicketID or SwiftTicketID) based on what is selected in fulfillmentMethod. */
  ticketCode?: string;
  fulfillmentMethod: ApplicantFulfillmentMethod;
  travellingCustomer: TravellingCustomer;
  swiftCard?: SwiftCardInfo;
  mTicket?: MTicket;
  student?: StudentInfo;
  occupation?: OccupationInfo;
}

export interface TravellingCustomer {
  /** Unique Customer ID for Travelling Customer (Known Applicant) */
  id?: string;
  firstName: string;
  lastName: string;
  /** ISO 8601 Date Format */
  dateOfBirth: string;
}

export interface SwiftCardInfo {
  /** State of SwiftCard respectively {An Existing already Registered, New Card to Register, New Card to Purchase} */
  state: SwiftCardInfoState;
  /** SwiftCard Number (Mandatory for Existing Swiftcard OR SwiftCard to Register - Optional for New SwiftCard) */
  cardNumber?: string;
  /** ISO 8601 Date Format - Start Date for SwiftCard */
  activationDate: string;
  newCardDeliveryAddress?: Address;
}

export interface Address {
  /** First Address Line */
  address1: string;
  /** Second Address Line */
  address2?: string;
  /** Locality or Town */
  town: string;
  /** Postal Code */
  postCode: string;
}

export interface MTicket {
  /** Mobile Number */
  mobileNumber: string;
  /** Email Address */
  emailAddress: string;
  /** ISO 8601 Date Format */
  startDate: string;
}

export interface StudentInfo {
  /** Student ID (AlphaNumeric) */
  studentId: string;
  /** College or University */
  institution: string;
  /** Title for Course */
  course: string;
  /** ISO 8601 Date Format - End Date for Course */
  courseEndDate: string;
}

export interface OccupationInfo {
  /** Occupation Type as {Apprentice or Trainee, Full-Time Education, Employment or Voluntary work} */
  occupationType: OccupationInfoOccupationType;
  providerName: string;
  providerAddress: Address;
}

export interface BillingCustomer {
  /** Billing Customer First Name */
  firstName: string;
  /** Billing Customer Last Name */
  lastName: string;
  /** ISO 8601 Date Format */
  dateOfBirth: string;
  /** Mobile Number Format 'ITU-T E.164' - Supported Internationally (Min length 7 and Max length 15 inclusive of both) */
  mobileNumber: string;
  /** Billing Customer Email */
  emailAddress: string;
}

export interface DirectDebit {
  /** Account Holder Name */
  fullName: string;
  /** Sort Code */
  sortCode: string;
  /** Account Number */
  accountNumber: string;
  /** Bank Name - Full (Received from Direct-Debit Verify API Response) */
  bankName: string;
  /** Bank Branch Name (Received from Direct-Debit Verify API Response) */
  branchName: string;
  /** Is Account Number Verified */
  accountVerified: boolean;
  /** Account Holder Direct Debit Mandate Confirmation */
  accountHolderMandate?: boolean;
  /** Credit Check Agreement Acceptance */
  creditCheckAgreement?: boolean;
}

export interface CheckoutPreferences {
  /** Keeping in touch for Upcoming Offers */
  upcomingOffers: boolean;
}

export interface TermsAndConditions {
  /** Terms &amp; Conditions Acceptance */
  acceptTerms: boolean;
  /** Legal Guardian Concent for users under 16 years of age (Nullable in case of No Child Tickets) */
  guardianConsent?: boolean;
}

export interface NotificationRedirect {
  /** Success URL to Redirect After Payment Notification is Received and Payment Authorized */
  successURL?: string;
  /** Error URL to Redirect After Payment Notification is Received and Payment Was not Successful */
  errorURL?: string;
}

export interface BasketCheckoutResponse {
  /** UUID Generated Unique for Each Basket */
  basketId: string;
  /** Order Number (NationalExpress Order Reference) */
  orderNumber: number;
  /** NationalExpress Order Number Status */
  orderStatus?: "PAYMENT_INITIATED";
  opayoCheckoutInfo: OpayoCheckoutInfo;
}

export interface OpayoCheckoutInfo {
  /** Opayo Transaction Identifier */
  txId: string;
  /** A URL to direct the customer's browser to Opayo hosted payments page */
  paymentPageURL: string;
}

export interface ApplicantImageUploadResponse {
  /** Image Resource URI */
  imageResource?: string;
  /** ISO 8601 Date Format */
  dateUploaded?: string;
}

export interface DDAccountVerifyRequest {
  /** Sort Code */
  sortCode: string;
  /** Bank Account Number */
  accountNumber: string;
}

export interface DDAccountVerifyResponse {
  /** Is Bank Account Valid respectively {Passes the validation rules, Fails the validation rules, No validation rules} for the selected bank */
  valid: DDAccountVerifyResponseValid;
  /** Bank Name - Full */
  bankName: string;
  /** Bank Branch Name */
  branchName: string;
  bankAddress: Address;
}

export interface PostcodeTicketVerifyRequest {
  /** Post Code */
  postCode: string;
  /** Ticket Product ID */
  ticketID: string;
  /** ISO 8601 Date Format - Travelling Customer (Applicant) Date of Birth */
  applicantDOB: string;
}

export interface PostcodeTicketVerifyResponse {
  /** Is Postcode Verified respectively {Verified to Match Ticket Type Criteria, UnVerified to Match Ticket Type Criteria} */
  status: PostcodeTicketVerifyResponseStatus;
}

export interface PathsHqj6FyCheckoutV1ApplicantPhotoPostRequestbodyContentMultipartFormDataSchema {
  image?: coreRestPipeline.RequestBodyType;
}

/** Defines headers for CheckoutAPI_applicantImageUpload operation. */
export interface CheckoutAPIApplicantImageUploadHeaders {
  /** Basket Id (UUID Unique for Each Basket) */
  basketId?: string;
  /** Basket Item Id (Ticket Product ID) */
  itemId?: string;
}

/** Known values of {@link HostOptions} that the service accepts. */
export enum KnownHostOptions {
  /** host: https:\//dt8nxbus.api.nationalexpress.com */
  HttpsDt8NxbusApiNationalexpressCom = "https://dt8nxbus.api.nationalexpress.com",
  /** host: https:\//perfnxbus.api.nationalexpress.com */
  HttpsPerfnxbusApiNationalexpressCom = "https://perfnxbus.api.nationalexpress.com",
  /** host: https:\//nxbus.api.nationalexpress.com */
  HttpsNxbusApiNationalexpressCom = "https://nxbus.api.nationalexpress.com"
}

/**
 * Defines values for HostOptions. \
 * {@link KnownHostOptions} can be used interchangeably with HostOptions,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **https:\//dt8nxbus.api.nationalexpress.com**: host: https:\/\/dt8nxbus.api.nationalexpress.com \
 * **https:\//perfnxbus.api.nationalexpress.com**: host: https:\/\/perfnxbus.api.nationalexpress.com \
 * **https:\//nxbus.api.nationalexpress.com**: host: https:\/\/nxbus.api.nationalexpress.com
 */
export type HostOptions = string;

/** Known values of {@link BasketItemRequestPaymentMethodType} that the service accepts. */
export enum KnownBasketItemRequestPaymentMethodType {
  /** OneOff */
  OneOff = "One Off",
  /** Subscription */
  Subscription = "Subscription"
}

/**
 * Defines values for BasketItemRequestPaymentMethodType. \
 * {@link KnownBasketItemRequestPaymentMethodType} can be used interchangeably with BasketItemRequestPaymentMethodType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **One Off** \
 * **Subscription**
 */
export type BasketItemRequestPaymentMethodType = string;

/** Known values of {@link BasketCurrency} that the service accepts. */
export enum KnownBasketCurrency {
  /** GBP */
  GBP = "GBP",
  /** EUR */
  EUR = "EUR",
  /** USD */
  USD = "USD"
}

/**
 * Defines values for BasketCurrency. \
 * {@link KnownBasketCurrency} can be used interchangeably with BasketCurrency,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **GBP** \
 * **EUR** \
 * **USD**
 */
export type BasketCurrency = string;

/** Known values of {@link BasketItemPaymentMethodType} that the service accepts. */
export enum KnownBasketItemPaymentMethodType {
  /** OneOff */
  OneOff = "One Off",
  /** Subscription */
  Subscription = "Subscription"
}

/**
 * Defines values for BasketItemPaymentMethodType. \
 * {@link KnownBasketItemPaymentMethodType} can be used interchangeably with BasketItemPaymentMethodType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **One Off** \
 * **Subscription**
 */
export type BasketItemPaymentMethodType = string;

/** Known values of {@link BasketDiscountRejectedReason} that the service accepts. */
export enum KnownBasketDiscountRejectedReason {
  /** VoucherCodeCustomerTypedTextIsNotValidPleaseCheckYourCodeForCouponNotFound */
  VoucherCodeCustomerTypedTextIsNotValidPleaseCheckYourCodeForCouponNotFound = 'Voucher code "customer typed text" is not valid. Please check your code. - #(For CouponNotFound)',
  /** VoucherCodeCustomerTypedTextHasAlreadyBeenRedeemedForCouponLimitReached */
  VoucherCodeCustomerTypedTextHasAlreadyBeenRedeemedForCouponLimitReached = 'Voucher code "customer typed text" has already been redeemed. - #(For CouponLimitReached)',
  /** VoucherCodeCustomerTypedTextCouldNotBeAppliedToThisJourneyPleaseCheckTheVoucherCodeCriteriaForCouponRejectedByCondition */
  VoucherCodeCustomerTypedTextCouldNotBeAppliedToThisJourneyPleaseCheckTheVoucherCodeCriteriaForCouponRejectedByCondition = 'Voucher code "customer typed text" could not be applied to this journey. Please check the voucher code criteria. - #(For CouponRejectedByCondition)',
  /** ManyMore */
  ManyMore = "..many more.."
}

/**
 * Defines values for BasketDiscountRejectedReason. \
 * {@link KnownBasketDiscountRejectedReason} can be used interchangeably with BasketDiscountRejectedReason,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Voucher code "customer typed text" is not valid. Please check your code. - #(For CouponNotFound)** \
 * **Voucher code "customer typed text" has already been redeemed. - #(For CouponLimitReached)** \
 * **Voucher code "customer typed text" could not be applied to this journey. Please check the voucher code criteria. - #(For CouponRejectedByCondition)** \
 * **..many more..**
 */
export type BasketDiscountRejectedReason = string;

/** Known values of {@link BasketItemUpdateRequestPaymentMethodType} that the service accepts. */
export enum KnownBasketItemUpdateRequestPaymentMethodType {
  /** OneOff */
  OneOff = "One Off",
  /** Subscription */
  Subscription = "Subscription"
}

/**
 * Defines values for BasketItemUpdateRequestPaymentMethodType. \
 * {@link KnownBasketItemUpdateRequestPaymentMethodType} can be used interchangeably with BasketItemUpdateRequestPaymentMethodType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **One Off** \
 * **Subscription**
 */
export type BasketItemUpdateRequestPaymentMethodType = string;

/** Known values of {@link ApplicantTicketFor} that the service accepts. */
export enum KnownApplicantTicketFor {
  /** Self */
  Self = "self",
  /** KnownApplicant */
  KnownApplicant = "knownApplicant",
  /** NewApplicant */
  NewApplicant = "newApplicant"
}

/**
 * Defines values for ApplicantTicketFor. \
 * {@link KnownApplicantTicketFor} can be used interchangeably with ApplicantTicketFor,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **self** \
 * **knownApplicant** \
 * **newApplicant**
 */
export type ApplicantTicketFor = string;

/** Known values of {@link ApplicantFulfillmentMethod} that the service accepts. */
export enum KnownApplicantFulfillmentMethod {
  /** MTicket */
  MTicket = "mTicket",
  /** Smartcard */
  Smartcard = "Smartcard"
}

/**
 * Defines values for ApplicantFulfillmentMethod. \
 * {@link KnownApplicantFulfillmentMethod} can be used interchangeably with ApplicantFulfillmentMethod,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **mTicket** \
 * **Smartcard**
 */
export type ApplicantFulfillmentMethod = string;

/** Known values of {@link SwiftCardInfoState} that the service accepts. */
export enum KnownSwiftCardInfoState {
  /** Existing */
  Existing = "Existing",
  /** Register */
  Register = "Register",
  /** New */
  New = "New"
}

/**
 * Defines values for SwiftCardInfoState. \
 * {@link KnownSwiftCardInfoState} can be used interchangeably with SwiftCardInfoState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Existing** \
 * **Register** \
 * **New**
 */
export type SwiftCardInfoState = string;

/** Known values of {@link OccupationInfoOccupationType} that the service accepts. */
export enum KnownOccupationInfoOccupationType {
  /** Trainee */
  Trainee = "Trainee",
  /** FullTime */
  FullTime = "FullTime",
  /** Voluntary */
  Voluntary = "Voluntary"
}

/**
 * Defines values for OccupationInfoOccupationType. \
 * {@link KnownOccupationInfoOccupationType} can be used interchangeably with OccupationInfoOccupationType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Trainee** \
 * **FullTime** \
 * **Voluntary**
 */
export type OccupationInfoOccupationType = string;

/** Known values of {@link DDAccountVerifyResponseValid} that the service accepts. */
export enum KnownDDAccountVerifyResponseValid {
  /** Valid */
  Valid = "Valid",
  /** Invalid */
  Invalid = "Invalid",
  /** NotValidated */
  NotValidated = "NotValidated"
}

/**
 * Defines values for DDAccountVerifyResponseValid. \
 * {@link KnownDDAccountVerifyResponseValid} can be used interchangeably with DDAccountVerifyResponseValid,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Valid** \
 * **Invalid** \
 * **NotValidated**
 */
export type DDAccountVerifyResponseValid = string;

/** Known values of {@link PostcodeTicketVerifyResponseStatus} that the service accepts. */
export enum KnownPostcodeTicketVerifyResponseStatus {
  /** Verified */
  Verified = "verified",
  /** Unverified */
  Unverified = "unverified"
}

/**
 * Defines values for PostcodeTicketVerifyResponseStatus. \
 * {@link KnownPostcodeTicketVerifyResponseStatus} can be used interchangeably with PostcodeTicketVerifyResponseStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **verified** \
 * **unverified**
 */
export type PostcodeTicketVerifyResponseStatus = string;

/** Optional parameters. */
export interface BasketCreateOptionalParams
  extends coreClient.OperationOptions {
  /** Create New Basket Request along with the First Item to be Added in Basket */
  body?: BasketItemRequest;
}

/** Contains response data for the basketCreate operation. */
export type BasketCreateResponse = Basket;

/** Optional parameters. */
export interface GetBasketByIDOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getBasketByID operation. */
export type GetBasketByIDResponse = Basket;

/** Optional parameters. */
export interface DeleteBasketByIDOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the deleteBasketByID operation. */
export type DeleteBasketByIDResponse = BasketDeleteResponse;

/** Optional parameters. */
export interface BasketAddItemOptionalParams
  extends coreClient.OperationOptions {
  /** Basket Add Item Request */
  body?: BasketItemRequest;
}

/** Contains response data for the basketAddItem operation. */
export type BasketAddItemResponse = Basket;

/** Optional parameters. */
export interface BasketUpdateItemOptionalParams
  extends coreClient.OperationOptions {
  /** Basket Update Item Request */
  body?: BasketItemUpdateRequest;
}

/** Contains response data for the basketUpdateItem operation. */
export type BasketUpdateItemResponse = Basket;

/** Optional parameters. */
export interface BasketDeleteItemOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the basketDeleteItem operation. */
export type BasketDeleteItemResponse = Basket;

/** Optional parameters. */
export interface BasketCheckoutOptionalParams
  extends coreClient.OperationOptions {
  /** Basket Checkout Request */
  body?: BasketCheckoutRequest;
}

/** Contains response data for the basketCheckout operation. */
export type BasketCheckoutOperationResponse = BasketCheckoutResponse;

/** Optional parameters. */
export interface ApplicantImageUploadOptionalParams
  extends coreClient.OperationOptions {
  image?: coreRestPipeline.RequestBodyType;
}

/** Contains response data for the applicantImageUpload operation. */
export type ApplicantImageUploadOperationResponse = CheckoutAPIApplicantImageUploadHeaders &
  ApplicantImageUploadResponse;

/** Optional parameters. */
export interface ApplicantImageOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the applicantImage operation. */
export type ApplicantImageResponse = {
  /**
   * BROWSER ONLY
   *
   * The response body as a browser Blob.
   * Always `undefined` in node.js.
   */
  blobBody?: Promise<Blob>;
  /**
   * NODEJS ONLY
   *
   * The response body as a node.js Readable stream.
   * Always `undefined` in the browser.
   */
  readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface DirectDebitAccountVerifyOptionalParams
  extends coreClient.OperationOptions {
  /** Direct-Debit Bank Account Verify Request */
  body?: DDAccountVerifyRequest;
}

/** Contains response data for the directDebitAccountVerify operation. */
export type DirectDebitAccountVerifyResponse = DDAccountVerifyResponse;

/** Optional parameters. */
export interface PostcodeTicketVerifyOptionalParams
  extends coreClient.OperationOptions {
  /** Postcode Ticket Mapping Verify Request */
  body?: PostcodeTicketVerifyRequest;
}

/** Contains response data for the postcodeTicketVerify operation. */
export type PostcodeTicketVerifyOperationResponse = PostcodeTicketVerifyResponse;

/** Optional parameters. */
export interface CheckoutAPIOptionalParams
  extends coreClient.ServiceClientOptions {
  /** server parameter */
  $host?: HostOptions;
  /** Overrides client endpoint. */
  endpoint?: string;
}
